/**
 * Parses `[POINT:...]` tags out of Claude's streaming response. The
 * tag is always emitted as the last thing in the message, but because
 * we stream, it may arrive mid-chunk — we have to keep parsing from
 * the end of the accumulated text and hide anything that looks like
 * an incomplete tag from the user-facing bubble.
 *
 * Three tag shapes are supported (in order of preference):
 *   [POINT:xN:label]         → ref-based (preferred — survives layout changes)
 *   [POINT:123,456:label]    → pixel-based (for purely visual targets)
 *   [POINT:none]             → pointing would not help
 */

import type { PointingTag } from "../types";

// Matches a COMPLETE tag only. Used for extraction after streaming ends.
const COMPLETED_TAG_REGEX = /\[POINT:([^\]]+)\]/;

// Matches the start of a possibly-incomplete tag. Used to hide partial
// tags from the streaming bubble so the user never sees "[POINT:x5…".
const PARTIAL_TAG_AT_END_REGEX = /\[POINT:[^\]]*$/;

export interface ParsedResponse {
  /** Clean text, safe to display to the user. */
  displayText: string;
  /** Pointing instruction, or null if no complete tag was found. */
  pointingTag: PointingTag | null;
}

/**
 * Strips any complete or partial `[POINT:...]` fragment from the
 * streaming text so the user-facing bubble never flashes raw metadata.
 * Does NOT parse the tag — that happens on the final completion.
 */
export function stripPointingTagFromStreamingText(text: string): string {
  // Full tag present — remove it entirely (including any trailing whitespace).
  const completeMatch = text.match(COMPLETED_TAG_REGEX);
  if (completeMatch) {
    const before = text.slice(0, completeMatch.index);
    const after = text.slice((completeMatch.index ?? 0) + completeMatch[0].length);
    return (before + after).replace(/\s+$/, "");
  }
  // Partial tag at the end — hide the partial substring so nothing leaks.
  return text.replace(PARTIAL_TAG_AT_END_REGEX, "").replace(/\s+$/, "");
}

/** Parse the final, completed response into display text + optional pointing tag. */
export function parseFinalResponse(text: string): ParsedResponse {
  const match = text.match(COMPLETED_TAG_REGEX);
  if (!match) {
    return { displayText: text.trim(), pointingTag: null };
  }
  const before = text.slice(0, match.index);
  const after = text.slice((match.index ?? 0) + match[0].length);
  const displayText = (before + after).trim();
  const pointingTag = parseTagBody(match[1]);
  return { displayText, pointingTag };
}

function parseTagBody(tagBody: string): PointingTag | null {
  const trimmed = tagBody.trim();
  if (trimmed.toLowerCase() === "none") return null;

  const parts = trimmed.split(":").map((segment) => segment.trim());
  // First part is either "xN" or "123,456". Remaining parts joined are the label.
  const [anchor, ...labelParts] = parts;
  const label = labelParts.join(":").trim() || "element";

  // Ref form: "x" followed by base36 characters.
  if (/^x[0-9a-z]+$/i.test(anchor)) {
    return { ref: anchor, pixelCoords: null, label };
  }

  // Pixel form: "int,int".
  const pixelMatch = anchor.match(/^(-?\d+)\s*,\s*(-?\d+)$/);
  if (pixelMatch) {
    return {
      ref: null,
      pixelCoords: {
        x: parseInt(pixelMatch[1], 10),
        y: parseInt(pixelMatch[2], 10),
      },
      label,
    };
  }

  return null;
}
